import * as React from "react";
import { CopyOutlined } from "@ant-design/icons";
import { Input, message, Space, Tooltip } from "antd";
import i18n, { I18N_NS } from "Config/i18n";
import { useTranslation } from "react-i18next";

const style: React.CSSProperties = {
  cursor: "pointer",
};

export const copyToClipboard = async (value: string): Promise<boolean> => {
  if (!value) {
    message.error(i18n.t("Sorry something went wrong.", { ns: I18N_NS.PROJECT }) as string);
    return false;
  }

  const copyUsingTextArea = (): boolean => {
    const textArea = document.createElement("textarea");
    textArea.value = value;
    textArea.setAttribute("readonly", "");
    textArea.style.position = "fixed";
    textArea.style.opacity = "0";
    textArea.style.pointerEvents = "none";
    textArea.style.top = "0";
    textArea.style.left = "0";
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    let isCopied = false;
    try {
      isCopied = document.execCommand("copy");
    } catch {
      isCopied = false;
    } finally {
      if (typeof textArea.remove === "function") {
        textArea.remove();
      } else {
        document.body.removeChild(textArea);
      }
    }
    return isCopied;
  };

  try {
    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(value);
      message.success(i18n.t("Copied", { ns: I18N_NS.PROJECT }) as string);
      return true;
    }
  } catch {
    // clipboard API failed, continue to execCommand fallback
  }

  const isCopied = copyUsingTextArea();
  if (isCopied) {
    message.success(i18n.t("Copied", { ns: I18N_NS.PROJECT }) as string);
    return true;
  }

  message.error(i18n.t("Sorry something went wrong.", { ns: I18N_NS.PROJECT }) as string);
  return false;
};

export const ClipboardInput = ({ value, disabled = true }: { value: string; disabled?: boolean }) => {
  const { t } = useTranslation(I18N_NS.PROJECT);
  const handleCopy = React.useCallback(() => {
    void copyToClipboard(value);
  }, [value]);

  return (
    <Space.Compact style={{ width: "100%" }}>
      <Space.Addon>
        <Tooltip title={t("Copied")} trigger="click" getPopupContainer={() => document.getElementById("root")}>
          <CopyOutlined style={style} onClick={handleCopy} />
        </Tooltip>
      </Space.Addon>
      <Input value={value} disabled={disabled} />
    </Space.Compact>
  );
};
